import React from 'react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconArrowUpRight,
  IconLockOutline,
  IconVerifiedBadge,
  Inline,
  Text,
  useDialogController
} from '@machobear/design-system'
import noop from 'lodash/noop'

import { getImageUrl } from '@/utils/image'
import { useEnsureLoggedIn } from '@/hooks/use-ensure-loggedIn'
import useSessionComplete from '@/hooks/use-session-complete'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import { setListingCardDataLayer } from '@/components/listing-card/utils'

import { ListingCardActions } from '../listing-card/actions'
import { useTermsDialogContext } from '../terms-dialog/context'
import { useListingCard } from './hooks/useListingCard'
import * as styles from './ListingCard.css'
import { ListingCardProps } from './ListingCard.types'
import { Badge, BadgeType } from './micro/Badge'
import { Carousel } from './micro/Carousel'
import FavouriteButton from './micro/FavouriteButton'

const defaultImageConfig = {
  fit: 'fit-in/500x300',
  quality: 80
}

export const ListingCardHorizontal: React.FC<ListingCardProps> = props => {
  const { t } = useTranslation()
  const { config } = useListingCard(props)
  const isLoggedIn = useSessionComplete()
  const {
    visible: loginModalVisible,
    showModal: showLoginModal,
    closeModal: closeLoginModal
  } = useDialogController()

  const { showModalForRequiredTerms } = useTermsDialogContext()

  const ensureLoggedIn = useEnsureLoggedIn({
    showLogin: showLoginModal
  })

  const {
    variant,
    imageUrls,
    address,
    listingType,
    listingCardDlv,
    ctaLabel,
    numberOfListings,
    suppressUnitAvailable,
    listingLink,
    verified,
    id,
    trackImpression = {},
    onHover,
    onClick,
    listing,
    displayPrice,
    displayDetails,
    displayAddress,
    displayCityRegion,
    isSingleListing,
    previousPrice
  } = props

  const buttonText = ctaLabel ?? config.ctaLabel
  const eventId = isLoggedIn
    ? 'listing_taptoview_click'
    : 'listing_logintoview_click'

  const renderLockedOverlay = () => {
    if (variant !== 'locked' || !config.isBlurred) return null

    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        background="customLight"
        className={styles.lockedOverlay}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: '8px'
        }}
      >
        <Box>
          <Button
            icon={isLoggedIn ? <IconArrowUpRight /> : <IconLockOutline />}
            variant="ghost"
            size="small"
            tone="neutral"
            id={eventId}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()

              setListingCardDataLayer({
                listing,
                featured: false,
                eventId
              })

              ensureLoggedIn(() =>
                showModalForRequiredTerms('trreb_agreements', () =>
                  window.open(listingLink, '_blank')
                )
              )
            }}
          >
            {isLoggedIn
              ? t('b.auth.view.tap_to_view', 'Tap to view')
              : t('b.auth.view.login_to_view', 'Log in to View')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      className={styles.root({ variant })}
      onMouseEnter={onHover}
      onClick={onClick}
      aria-label={`Most relevant listing ${id}`}
      data-impression={trackImpression.enabled}
      data-impression-listingid={id}
      data-impression-page={trackImpression.page}
      data-impression-section={trackImpression.section}
      data-impression-sponsored={trackImpression.sponsored}
      display="flex"
      background="white"
      borderRadius="standard"
      overflow="hidden"
      cursor="pointer"
      border="borderNeutralLight"
      boxShadow="xsmall"
      style={{
        height: '200px',
        width: '100%',
        flexDirection: 'row'
      }}
    >
      {/* Image Section - Left Side */}
      <Box
        style={{
          width: '240px',
          height: '100%',
          position: 'relative',
          flexShrink: 0
        }}
      >
        <Box
          style={{
            position: 'relative',
            width: '100%',
            height: '100%'
          }}
        >
          {/* Image Carousel */}
          {imageUrls && imageUrls.length > 0 && (
            <Carousel imageUrls={imageUrls} lazyLoad={true}>
              {(key: string) => (
                <Image
                  key={key}
                  src={getImageUrl(key, defaultImageConfig)}
                  alt={address || ''}
                  fill
                  style={{ objectFit: 'cover' }}
                  sizes="240px"
                  priority={true}
                />
              )}
            </Carousel>
          )}

          {/* Badges */}
          <Box
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              zIndex: 2
            }}
          >
            {Array.from(config.badges).map((type, i) => {
              // suppress the units available badge in multi-listing modal
              if (type === 'unitsAvailable' && suppressUnitAvailable) {
                return null
              }

              return (
                <Badge
                  key={i}
                  type={type as BadgeType}
                  count={
                    type === 'unitsAvailable' ? numberOfListings : undefined
                  }
                />
              )
            })}
          </Box>

          {/* Checkbox for select variant */}
          {config.showCheckbox && variant === 'select' && (
            <Box
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 2
              }}
              onClick={e => e.stopPropagation()}
            >
              <Checkbox
                checked={props.isSelected}
                onChange={props.onSelectToggle}
                label=""
                id="select"
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Content Section - Right Side */}
      <Box
        display="flex"
        flexDirection="column"
        padding="medium"
        justifyContent="spaceBetween"
        gap="small"
        style={{
          flex: 1
        }}
      >
        {/* Top Content */}
        <Box display="flex" flexDirection="column" gap="small">
          <Box
            display="flex"
            justifyContent="spaceBetween"
            alignItems="flexStart"
          >
            <Box display="flex" flexDirection="column" gap="small">
              <Box display="flex" gap="xxxsmall" alignItems="center">
                {previousPrice && (
                  <Text tone="secondary">
                    <s>${previousPrice}</s>
                  </Text>
                )}
                <Text
                  weight="strong"
                  size="medium"
                  tone={previousPrice ? 'critical' : 'neutral'}
                >
                  {displayPrice.price}
                </Text>
                <Text size="small">{displayPrice.period}</Text>
              </Box>
              <Text tone="secondary" size="small" weight="medium">
                {displayDetails}
              </Text>
            </Box>

            {isSingleListing && (
              <FavouriteButton
                listingId={id}
                eventId="listing-card-click-save"
                showDSLoginModal={showLoginModal}
                listingCardDlv={listingCardDlv}
              />
            )}
          </Box>

          <Divider />

          <Box display="flex" alignItems="center" gap="xxsmall">
            <Text size="small" weight="medium">
              {displayAddress}
            </Text>
            {verified && (
              <Text size="xsmall" tone="brandAccent">
                <IconVerifiedBadge alignY="lowercase" />
              </Text>
            )}
          </Box>
          <Text size="xsmall">
            {`${displayCityRegion} ${listingType ? `· ${listingType}` : ''}`}
          </Text>
        </Box>

        {/* Bottom Actions */}
        <Box style={{ marginTop: 'auto' }}>
          {isSingleListing ? (
            <ListingCardActions
              listing={listing}
              isCustomCompanyPage={false}
              customBaseUrl={''}
              loading={false}
              featured={false}
              stackButtons={false}
              onComplete={noop}
            />
          ) : (
            <Button variant="solid" onClick={onClick} size="small">
              {buttonText}
            </Button>
          )}
        </Box>
      </Box>

      {/* Locked overlay - positioned over entire card */}
      {renderLockedOverlay()}

      {/* Login Modal */}
      <DSLoginModal
        mode="login"
        eventId="listing-card-login-modal"
        visible={loginModalVisible}
        onClose={closeLoginModal}
        t={t}
      />
    </Box>
  )
}
